import React, { memo, useCallback, useMemo } from 'react'
import {
  Table as BaseTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/Table'
import Select from '../ui/Select/Select'
import Badge from '../ui/Badge'
import Text from '../ui/Text'
import ResourceCell from './ResourceCell'
import Card from '../ui/Card'
import { PROGRESSION_TABLE_CLASS, PROGRESSION_TABLE_HEAD_CLASS } from './constants'
import EmptyState from '../ui/EmptyState'
import Loader from '../shared/Loader'
import Pagination, { PER_PAGE_OPTIONS } from '../ui/pagination'
import ProgressionService from '../../services/api/ProgressionService'
import { formatDateTime } from '../../utils/dateHelpers'
import { usePaginatedTable } from './hooks'

const EMPTY_MESSAGE = 'Les opérateurs apparaîtront ici une fois les données disponibles.'

const COLUMN_WIDTHS_WITH_VARIABLE = {
  variable: 'tw:w-[15%]',
  user: 'tw:w-[22%]',
  progression: 'tw:w-[18%]',
  resource: 'tw:w-[15%]',
  section: 'tw:w-[15%]',
  date: 'tw:w-[15%]'
}

const COLUMN_WIDTHS_WITHOUT_VARIABLE = {
  user: 'tw:w-[21%]',
  progression: 'tw:w-[18%]',
  resource: 'tw:w-[20%]',
  section: 'tw:w-[19%]',
  date: 'tw:w-[22%]'
}

const PROGRESSION_VARIANTS = { success: 'success', inProgress: 'progress', danger: 'danger' }
const PROGRESSION_SUCCESS_THRESHOLD = 0.5

const progressionVariant = ratio => {
  if (ratio >= PROGRESSION_SUCCESS_THRESHOLD)
    return PROGRESSION_VARIANTS.success
  if (ratio > 0)
    return PROGRESSION_VARIANTS.inProgress
  return PROGRESSION_VARIANTS.danger
}

const OperatorsProgressionTable = ({
  collectId,
  dateRange,
  draftId,
  operatorId,
  resourceSearch,
  variableId,
  variables,
  onVariableChange
}) => {
  const variableOptions = useMemo(() => {
    const list = variables || []
    return list.map(variable => ({ value: variable.id, label: variable.name }))
  }, [variables])

  const selectedVariable = useMemo(() => {
    const match = variableOptions.find(option => String(option.value) === String(variableId))
    return match || null
  }, [variableOptions, variableId])
  const widths = selectedVariable ? COLUMN_WIDTHS_WITH_VARIABLE : COLUMN_WIDTHS_WITHOUT_VARIABLE
  const columnCount = selectedVariable ? 6 : 5

  const fetcher = useCallback(({ page, perPage, sortBy, sortDirection }) => ProgressionService
    .fetchList(collectId, draftId, {
      name: 'operator_progress',
      variableId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      operatorId,
      resourceSearch,
      page,
      perPage,
      sortBy,
      sortDirection
    }), [collectId, draftId, dateRange.startDate, dateRange.endDate, variableId, operatorId, resourceSearch])

  const filtersKey = useMemo(() => JSON.stringify({
    variableId,
    operatorId,
    resourceSearch,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  }), [variableId, operatorId, resourceSearch, dateRange.startDate, dateRange.endDate])

  const {
    rows, isLoading, sortProps,
    page, perPage, total, lastPage, setPage, setPerPage
  } = usePaginatedTable({ fetcher, rowsKey: 'rows', filtersKey })

  const variableSelect = (
    <div className="tw:w-64">
      <Select
        options={variableOptions}
        placeholder="Choisir une variable"
        value={selectedVariable}
        isClearable
        onChange={onVariableChange}
      />
    </div>
  )

  const isEmpty = rows.length === 0

  const renderVolumeCell = row => {
    if (!draftId)
      return row.answers_count
    const totalResources = row.total_resources || 0
    const ratio = totalResources > 0 ? row.finished_resources / totalResources : 0
    const pct = Math.round(ratio * 100)
    return (
      <>
        <span className="tw:mr-1">{`${row.finished_resources}/${row.total_resources}`}</span>
        <Badge variant={progressionVariant(ratio)}>{`${pct} %`}</Badge>
      </>
    )
  }

  const renderBody = () => {
    if (isEmpty) {
      return (
        <TableRow>
          <TableCell colSpan={columnCount}>
            {isLoading ? <div className="tw:h-52" /> : <EmptyState message={EMPTY_MESSAGE} />}
          </TableCell>
        </TableRow>
      )
    }
    return rows.map(row => (
      <TableRow key={row.user_id}>
        {selectedVariable ? (
          <TableCell>
            <Text lines={2}>{row.last_resource_variable_value || '-'}</Text>
          </TableCell>
        ) : null}
        <TableCell>
          <Text lines={2}>{row.user_name}</Text>
        </TableCell>
        <TableCell>{renderVolumeCell(row)}</TableCell>
        <TableCell>
          <ResourceCell
            collectId={collectId}
            draftId={draftId || row.last_draft_id}
            name={row.last_resource_name}
            resourceId={row.last_resource_id}
          />
        </TableCell>
        <TableCell>
          <Text lines={2}>{row.last_section_name || '-'}</Text>
        </TableCell>
        <TableCell>{formatDateTime(row.last_updated_at)}</TableCell>
      </TableRow>
    ))
  }

  return (
    <Card headerRight={variableSelect} title="Opérateurs" plainHeader>
      <div className="tw:relative tw:overflow-x-auto">
        <BaseTable className={PROGRESSION_TABLE_CLASS} hover={!isEmpty} bordered>
          <TableHead className={PROGRESSION_TABLE_HEAD_CLASS}>
            <TableRow>
              {selectedVariable ? (
                <TableHeader
                  className={widths.variable}
                  {...sortProps('last_resource_variable_value')}
                >
                  {selectedVariable.label}
                </TableHeader>
              ) : null}
              <TableHeader className={widths.user} {...sortProps('user_name')}>
                Opérateurs
              </TableHeader>
              <TableHeader
                className={widths.progression}
                {...sortProps(draftId ? 'progression' : 'answers_count')}
              >
                {draftId ? 'Progression' : 'Réponses'}
              </TableHeader>
              <TableHeader className={widths.resource} {...sortProps('last_resource_name')}>
                Dernière ressource
              </TableHeader>
              <TableHeader className={widths.section} {...sortProps('last_section_name')}>
                Dernière section
              </TableHeader>
              <TableHeader className={widths.date} {...sortProps('date')}>
                Date
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </BaseTable>
        {isLoading ? (
          <div
            className={'tw:absolute tw:inset-x-0 tw:bottom-0 tw:top-12 tw:flex tw:items-center '
              + 'tw:justify-center tw:bg-white/70 tw:z-10'}
          >
            <Loader />
          </div>
        ) : null}
      </div>
      {isEmpty ? null : (
        <Pagination
          disabled={isLoading}
          lastPage={lastPage}
          page={page}
          perPage={perPage}
          perPageOptions={PER_PAGE_OPTIONS}
          total={total}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
    </Card>
  )
}

export default memo(OperatorsProgressionTable)
